import { DestroyRef, Injectable, inject } from '@angular/core';
import { LogEvent } from '../../enums/log-event';
import { LoggerService } from '../logger.service';
import { MessagePayload } from 'firebase/messaging';
import { environment } from '../../../../environments/environment';
import { NotificationService } from './notification.service';
import { UserDevicesService } from './user-devices.service';
import { firebaseWrapper } from './firebase.wrapper';
import { NAVIGATOR_TOKEN } from 'src/app/shared/utils/navigator-token';
import { catchError, from, of } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UserNotificationStateService } from './user-notification-state.service';
import { ToastService } from 'asksuite-citrus';
import { LocalizationService } from '../localization.service';
import { StorageUtilService } from '../storage-util.service';

export type MessageEvent = {
  data: {
    type: string;
    notificationData: {
      data: {
        companyId: string;
        groupId: string;
        adminAction: string;
      };
      body: string;
      icon: string;
      title: string;
      clickAction: string;
    };
  };
};

@Injectable({
  providedIn: 'root',
})
export class PushNotificationService {
  protected deviceToken = '';

  private destroyRef = inject(DestroyRef);
  private navigator = inject(NAVIGATOR_TOKEN);
  keyNotificationToastBlocked= 'enabled_toast_blocked'

  constructor(
    private logger: LoggerService,
    private notificationService: NotificationService,
    private userDeviceService: UserDevicesService,
    private userNotificationStateService: UserNotificationStateService,
    private toastService: ToastService,
    private localizationService: LocalizationService,
    private storageService: StorageUtilService<boolean>
  ) {
    this.userNotificationStateService.userDeviceCurrentStatus$.subscribe((status) => {
      if (status === null || status) return;
      this.localizationService.keyAsync('NOTIFICATION.TOAST_DISABLED').pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value)=> this.toastService.warning(value,{
        timeout:8000
      }))
    });
  }

  start() {
    if (!('Notification' in window)) {
      this.logger.log(LogEvent.LIVECHAT_NOTIFICATION_BROWSER_WITHOUT_HANDLER);
      return;
    }
    this.requestPermission();
  }

  private requestPermission() {
    if (window.Notification.permission === 'granted') {
      this.userNotificationStateService.setNotificationPermission('granted');
      this.handleTokenRetrival();
      return;
    }

    from(Notification.requestPermission())
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError((error) => {
          this.logger.error(
            LogEvent.LIVECHAT_NOTIFICATION_PERMISSION_REQUEST_FAILED,
            error
          );
          return of(null);
        })
      )
      .subscribe((status) => {
        if (!status) return;

        this.userNotificationStateService.setNotificationPermission(status);
        const visible = this.storageService.getItemWithExpiration(this.keyNotificationToastBlocked) !== false ? true : false;
        if (status === 'granted' && visible) {
          this.handleTokenRetrival();
        } else if(visible) {
          this.localizationService.keyAsync('NOTIFICATION.TOAST_BLOCKED').subscribe((value)=> this.toastService.warning(value,{
            timeout:8000
          }))
          this.storageService.setItemWithExpiration(this.keyNotificationToastBlocked, false, 7)
        }
      });
  }

  private handleTokenRetrival(): void {
    this.getToken()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError((error) => {
          this.logger.error(
            LogEvent.LIVECHAT_NOTIFICATION_GET_TOKEN_FAILED,
            error
          );
          this.userNotificationStateService.setNotificationRegisterError(
            'on_get_token'
          );
          return of(null);
        })
      )
      .subscribe((currentToken) => {
        if (!currentToken) {
          this.logger.log(LogEvent.LIVECHAT_NOTIFICATION_WITHOUT_VALID_TOKEN, {
            message:
              'No registration token available. Request permission to generate one.',
          });
          return;
        }

        this.saveToken(currentToken);
        this.startListening();
      });
  }

  private getToken() {
    const messaging = firebaseWrapper.getMessaging();
    return from(
      firebaseWrapper.getToken(messaging, {
        vapidKey: environment.fcmPushNotifications.vapidKey,
      })
    );
  }

  private saveToken(currentToken: string) {
    this.userDeviceService.saveToken(currentToken);
    this.logger.log(LogEvent.LIVECHAT_NOTIFICATION_GET_TOKEN, {
      token: currentToken,
    });
  }

  private startListening() {
    const messaging = firebaseWrapper.getMessaging();
    // When the app is the current tab, this one is triggered
    firebaseWrapper.onMessage(messaging, (payload: MessagePayload) => {
      if (payload.notification && payload.notification.body) {
        this.logger.log(LogEvent.LIVECHAT_NOTIFICATION_PUSH_ARRIVING, payload);

        this.notificationService.sendNotification({
          message: payload.notification.body || '',
          timeout: 30 * 1000,
          notification: payload,
          type: 'push_notification_on_focus',
        });
      }
    });
    this.navigator.serviceWorker.addEventListener(
      'message',
      (event: MessageEvent) => {
        const data = event.data;
        if (data.type === 'push_notification_click') {
          const {
            data: { adminAction },
            clickAction,
          } = data.notificationData;

          if (clickAction && adminAction === 'open_attendance_live_chat') {
            const urlToOpen = `${environment.app.url}${clickAction}`;
            window.open(urlToOpen, '_blank');
          }
        }

        if (data.type === 'incoming_push') {
          this.logger.log(
            LogEvent.LIVECHAT_NOTIFICATION_PUSH_ARRIVING_BACKGROUND,
            data.notificationData
          );
        }
      }
    );
  }
}
